'use client';

import * as path from 'path';

import * as ENGINE from '@gnsx/genesys.js';
import CRC32 from 'crc-32';

import { globalStore } from '@/app/globalStore';
import { DEBUG_SKIP_LOAD_EDITOR_WORLD } from '@/const';
import { actorParenting, objectHelpers } from '@/editor/core';
import { importNavMeshSettings, NavMeshEventEmitter } from '@/editor/core/actions/navmeshActions';
import { normaliseScenePathForComparison } from '@/editor/core/actions/sceneRenameUtils';
import { resolveSceneUndoStackKey, syncActiveUndoStack } from '@/editor/core/undoRedo';
import { sceneStore, store } from '@/editor/store';
import { scheduleDeferredInitialScreenshotIfMissing } from '@/editor/store/sceneStore';
import { IS_DEV } from '@/lib/utils';

const DEBUG_LOG = {
  saveScene: true && IS_DEV,
  performance: true && IS_DEV,
} as const;

export const getStorageProvider = () => {
  const storageProvider = store.project.makeScopedStorageProvider();
  return storageProvider;
};

/**
 * Saves the current scene state to storage
 * @param scenePath The scene path to save to, defaults to the current scene in the project
 * @returns Promise that resolves with the saved file path
 * @throws Error if userId is not provided
 */
export const saveScene = async (scenePath: ENGINE.AssetPath | null = null): Promise<string> => {
  if (DEBUG_SKIP_LOAD_EDITOR_WORLD) {
    console.log('[saveScene] DEBUG_SKIP_LOAD_EDITOR_WORLD is enabled, skipping save');
    return '';
  }

  const logTime = (label: string, start: number) => { if (DEBUG_LOG.performance) console.log(`💾 [saveScene] ${label} took ${(performance.now() - start).toFixed(2)}ms`); };
  let start = performance.now();

  scenePath ??= store.project.currentScene ? ENGINE.AssetPath.fromString(store.project.currentScene) : null;
  if (!scenePath) throw new Error('No scene path provided');

  const storageProvider = getStorageProvider();
  try {
    let selectionGroupActors: ENGINE.Actor[] = [];
    let detachedParentingEntries: ReturnType<typeof actorParenting.detachAllParenting> | null = null;
    const scene = sceneStore.scene;
    const world = sceneStore.world;

    if (world == null || scene == null) {
      const missingState =
        world == null && scene == null
          ? 'world and scene'
          : world == null
            ? 'world'
            : 'scene';
      throw new Error(`[sceneHandler:saveScene] Cannot save scene because the editor ${missingState} is not loaded`);
    }

    // In order to save actors correctly, we need to detach the group children and reattach them to the world
    // This is because the group children are not part of the world, and we need to save the world with the correct positions
    // This is a hack, and we need to find a better way to do this.
    start = performance.now();
    selectionGroupActors = sceneStore.selectionGroup.children
      .filter((child): child is ENGINE.SceneComponent => child instanceof ENGINE.SceneComponent)
      .map(component => component.getActor())
      .filter((actor): actor is ENGINE.Actor => actor !== null);

    objectHelpers.detachGroupChildren(sceneStore.selectionGroup, scene);
    logTime('detachGroupChildren', start);

    actorParenting.validateAndRepairAttachments(world);
    start = performance.now();
    detachedParentingEntries = actorParenting.detachAllParenting(world, scene);
    logTime('detachAllParenting', start);

    let worldData: ENGINE.SerializedData;
    try {
      start = performance.now();
      worldData = ENGINE.WorldSerializer.exportObject(world);
      logTime('WorldSerializer.exportObject', start);
    } finally {
      if (detachedParentingEntries) {
        start = performance.now();
        actorParenting.reattachAllParenting(detachedParentingEntries, world);
        logTime('reattachAllParenting', start);
      }

      if (selectionGroupActors.length > 0) {
        start = performance.now();
        objectHelpers.attachToGroup(sceneStore.selectionGroup, selectionGroupActors);
        logTime('attachToGroup', start);
      }
    }

    // Create JSON string with pretty formatting
    start = performance.now();
    const worldDataText = JSON.stringify(worldData, null, 2);
    logTime('JSON.stringify', start);

    // Generate file path based on timestamp and user ID
    console.log('Saving scene to:', scenePath);
    start = performance.now();
    const parentFolder = path.dirname(scenePath.initialPath);
    await storageProvider.createDirectory(ENGINE.AssetPath.fromString(parentFolder));
    logTime('createDirectory', start);

    start = performance.now();
    store.project.setCurrentScene(scenePath.initialPath, worldDataText);
    logTime('setCurrentScene', start);

    start = performance.now();
    sceneStore.saveCameraState('', true);
    logTime('saveCameraState + captureScreenshot', start);

    // Upload the file using the storage provider
    start = performance.now();
    const result = await storageProvider.uploadFile(
      scenePath,
      new Blob([worldDataText], { type: 'application/json' })
    );
    logTime('uploadFile', start);

    return result.path;
  } catch (error) {
    console.error('[sceneHandler:saveScene] Failed to save scene:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('[sceneHandler:saveScene] Failed to save scene');
  }
};



export const processWorldDataForLoad = async (
  worldData: ENGINE.SerializedData
): Promise<ENGINE.SerializedData> => {
  const storageProvider = getStorageProvider();
  const prefabNames = ENGINE.WorldSerializer.listPrefabPaths(worldData);

  async function downloadPrefabs(
    names: string[]
  ): Promise<Record<string, ENGINE.SerializedData>> {
    const prefabsData: Record<string, ENGINE.SerializedData> = {};
    for (const prefabName of names) {
      try {
        const prefabData = (await storageProvider.downloadFileAsJson(
          ENGINE.AssetPath.fromString(prefabName)
        )) as ENGINE.SerializedData;
        prefabsData[prefabName] = prefabData;
      } catch (error) {
        console.error('Failed to download prefab:', error);
      }
    }
    return prefabsData;
  }

  const prefabsData = await downloadPrefabs(prefabNames);
  store.assets.setPrefabData(prefabsData);
  return worldData;
};

async function importConfiguredNavMesh(storageProvider: ReturnType<typeof getStorageProvider>): Promise<void> {
  const world = sceneStore.world;
  if (!world) {
    return;
  }

  const navMeshResourcePath = world.getSceneResources()?.navMesh?.path;
  if (!navMeshResourcePath) {
    return;
  }

  try {
    const navMeshBuffer = await storageProvider.downloadFileAsBuffer(
      ENGINE.AssetPath.fromString(navMeshResourcePath)
    );
    const imported = await world.importNavigationMeshFromData(navMeshBuffer);
    if (!imported) {
      console.warn('[sceneHandler] NavMesh resource exists but import failed:', navMeshResourcePath);
    }
  } catch (error) {
    console.warn('[sceneHandler] Failed to import configured NavMesh resource:', navMeshResourcePath, error);
  }
}

/**
 * Loads a scene from storage and creates the actors in the world
 * @param filePath The path to the scene file
 * @returns Promise that resolves when the scene is loaded
 */
export const loadScene = async (
  filePath: ENGINE.AssetPath,
  skipIfCrc32Matches: boolean = false,
  options: { recreateCanvas?: boolean } = {}
): Promise<void> => {
  const storageProvider = getStorageProvider();
  // Always recreate the canvas when loading a scene, there's some render issues when not doing this
  const { recreateCanvas = false } = options;

  // Detect whether this is a reload of the scene that is already open. If so we need to discard the
  // current undo stack (world state is being replaced from disk, so existing undo entries are stale).
  const isReload =
    !!store.project.currentScene &&
    normaliseScenePathForComparison(store.project.currentScene) ===
      normaliseScenePathForComparison(filePath.initialPath);

  try {
    globalStore.showLoadingOverlay('loadScene', { message: 'Loading Scene...', pinned: true });

    // Download and parse the scene file
    const worldDataText: string = await storageProvider.downloadFileAsText(filePath);

    // Skip if the CRC32 matches the current scene's CRC32
    if (skipIfCrc32Matches && store.project.currentSceneCrc32 === CRC32.str(worldDataText)) {
      console.log(`Skipping scene load: ${filePath} because CRC32 matches`);
      return;
    }

    if (DEBUG_SKIP_LOAD_EDITOR_WORLD) {
      console.log('[EditorStore] DEBUG_SKIP_LOAD_EDITOR_WORLD is enabled, skipping startup scene load');
      store.project.setCurrentScene(filePath.initialPath, null);
      syncActiveUndoStack();
      return;
    }

    const worldData = ENGINE.parseJson(worldDataText) as ENGINE.SerializedData;
    const preparedWorldData = await processWorldDataForLoad(worldData);
    await sceneStore.resetWorld(preparedWorldData, { resetCanvas: recreateCanvas });
    await importConfiguredNavMesh(storageProvider);
    await importNavMeshSettings();

    if (sceneStore.world) {
      await ENGINE.tryApplyLightmapsAfterSceneReady(sceneStore.world, filePath.initialPath);
    }

    store.project.setCurrentScene(filePath.initialPath, worldDataText);
    sceneStore.updateOutliner();

    // Switch the active undo stack to this scene. For a same-scene reload the stack is cleared
    // first because the world state has been replaced from disk and old entries would be stale.
    if (isReload) {
      store.undoRedoManager.clearStack(resolveSceneUndoStackKey(filePath.initialPath));
    }
    syncActiveUndoStack();

    // Emit navmesh-changed event to notify UI that a navmesh may have been loaded
    NavMeshEventEmitter.getInstance().emitNavMeshChanged();

    scheduleDeferredInitialScreenshotIfMissing();
  } catch (error) {
    console.error('Failed to load scene:', error);
    throw new Error('Failed to load scene');
  } finally {
    globalStore.hideLoadingOverlay('loadScene');
  }
};

/**
 * Configures scene resources for automatic loading
 * This should be called when exporting lightmaps or navmesh to update the scene file
 */
export const configureSceneResources = async (): Promise<void> => {
  if (!sceneStore.world || !store.project.currentScene) {
    console.log('[configureSceneResources] No scene loaded');
    return;
  }

  try {
    console.log('[configureSceneResources] Scene resources have been configured');
    console.log('[configureSceneResources] Save the scene to persist lightmap/navmesh resource paths');
  } catch (error) {
    console.error('[configureSceneResources] Failed to configure scene resources:', error);
  }
};


export const resetScene = async (): Promise<void> => {
  globalStore.showLoadingOverlay('resetScene', { message: 'resetting scene' });
  try {
    await sceneStore.resetWorld();
    store.project.setCurrentScene(null, null);
    syncActiveUndoStack();
  } catch (error) {
    console.error('[sceneHandler:resetScene] Failed to reset scene:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('[sceneHandler:resetScene] Failed to reset scene');
  } finally {
    globalStore.hideLoadingOverlay('resetScene');
  }
};